// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs"
import { recordClientErrorCensus } from './lib/client-error-census'
import { shouldIgnoreClientSentryEvent } from './lib/sentry-noise'

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Only enable in production
  enabled: process.env.NODE_ENV === "production",

  // Retain production performance visibility without sending a trace for one
  // in every ten sessions on this high-traffic, mostly static site.
  tracesSampleRate: 0.01,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Session Replay is intentionally NOT enabled on the client.
  //
  // Replay (~136 KB) + the Feedback widget (~37 KB) it transitively retains are
  // the single largest reducible client dependency on this public, no-auth,
  // no-PII content site. Sentry's `@sentry/nextjs` barrel keeps both in the
  // eager client bundle under Turbopack regardless of whether they are
  // referenced (lazyLoadIntegration / dynamic import do NOT tree-shake them
  // out — verified empirically, TES-838). They are stripped at build time via
  // `bundleSizeOptimizations` in next.config.ts instead. Error stack traces,
  // breadcrumbs, and performance tracing remain fully intact, which is the
  // observability this site actually needs.
  integrations: [
    Sentry.browserTracingIntegration(),
  ],

  // Filter out known non-critical errors
  beforeSend(event, hint) {
    if (shouldIgnoreClientSentryEvent(event, hint)) {
      return null
    }

    // Everything past this point is an event we intend to bill. While the org
    // quota is exhausted (through the 2026-09-15 reset) these are counted and
    // discarded rather than stored, so they cannot be read back from Sentry —
    // the census writes a free one-line summary to the Vercel runtime log so
    // the residual stream can be named without spending quota (TES-2004).
    // Deliberately after the noise filter: it measures what we actually offer.
    recordClientErrorCensus(event)

    return event
  },

  // Set environment
  environment: process.env.NODE_ENV,

  // Release tracking (set in CI/CD)
  release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
})
